import { Check, ChevronDown, Phone } from 'lucide-react';
import { useDismissOnOutside } from '../../lib/useDismiss';
import type { CallOption } from './callOptions';
import { InfoTip } from './InfoTip';

/**
 * One route in a call menu: its name, and a "?" that says what it does and
 * which number the customer will see. That used to be a second line under every
 * row, which doubled the menu's height and ran out of the card on a long number.
 *
 * The reason a route cannot run is NOT behind the "?": it stays on the row, so
 * an agent finds out here and not from a red banner after the press, and a
 * greyed row never sits there without saying why.
 *
 * The "?" is a sibling of the row's button rather than inside it — a button
 * cannot hold another — and is laid over the row's right edge (see the CSS).
 */
export function CallMenuRow({
  label,
  about,
  blocked,
  picked = false,
  onPick,
}: {
  label: string;
  /** What the route does and the number it presents, for the "?". */
  about: string;
  /** Why it cannot run, or null when it can. */
  blocked: string | null;
  /** Ticked: the route the Call button itself takes. */
  picked?: boolean;
  onPick: () => void;
}) {
  return (
    <div className="thr-call-row">
      <button type="button" disabled={Boolean(blocked)} onClick={onPick}>
        {picked ? <Check size={13} /> : <span style={{ width: 13 }} />}
        <span className="thr-call-opt">
          <span>{label}</span>
          {blocked && <span className="thr-call-opt-sub">{blocked}</span>}
        </span>
      </button>
      <InfoTip text={about} label={`About ${label}`} />
    </div>
  );
}

/**
 * The Call control: the route it picked, plus the others behind a caret.
 *
 * Shared by the thread header and the call log, because "which line does this
 * leave on" is the same question in both places and the answer must not drift
 * between them. Menu state is the caller's: a thread header closes its other
 * menus when this one opens, a table closes the other rows'.
 */
export function CallButton({
  options,
  to,
  open,
  onOpenChange,
}: {
  options: CallOption[];
  /** The number being called, for the button's own title. */
  to: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const menuRef = useDismissOnOutside<HTMLDivElement>(open, () => onOpenChange(false));

  // The first route that can actually run. A blocked default must not swallow
  // the press — the menu is right there saying which one it took instead.
  const primary = options.find((option) => !option.blocked) ?? options[0] ?? null;
  if (!primary) return null;

  return (
    <div className="thr-head-menu thr-call">
      <button
        type="button"
        className="cbtn thr-call-main"
        disabled={Boolean(primary.blocked)}
        title={primary.blocked ?? `Call ${to} from ${primary.from} · ${primary.hint}`}
        onClick={() => {
          onOpenChange(false);
          primary.run();
        }}
      >
        <Phone size={16} style={{ color: 'var(--c-fg)' }} />
        <span className="cbtn-label">Call</span>
      </button>
      {(options.length > 1 || primary.blocked) && (
        <button
          type="button"
          className="cbtn cbtn-icon thr-call-more"
          title="Call from…"
          onClick={() => onOpenChange(!open)}
        >
          <ChevronDown size={14} />
        </button>
      )}
      {open && (
        <div className="thr-menu thr-call-menu" ref={menuRef}>
          {options.map((option) => (
            <CallMenuRow
              key={option.key}
              label={option.label}
              about={`From ${option.from} · ${option.hint}`}
              blocked={option.blocked}
              picked={option.key === primary.key}
              onPick={() => {
                onOpenChange(false);
                option.run();
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}
